import { useFonts } from '@folklore/fonts';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useCallback, useState } from 'react';

import * as AppPropTypes from '../../lib/PropTypes';

import { useHeader, useLoading } from '../../contexts/SiteContext';
import { useFloatingVideo } from '../../contexts/VideoContext';
import Header from '../headers/Header';
import Modal from '../modals/Modal';
import Detector from '../partials/Detector';
// import Footer from '../partials/Footer';
import FloatingVideo from '../partials/FloatingVideo';
import FloatingVideoSlot from '../partials/FloatingVideoSlot';
import NewsletterFooter from '../partials/NewsletterFooter';

import styles from '../../../styles/layouts/main.module.scss';

const propTypes = {
    page: AppPropTypes.page,
    fonts: PropTypes.shape({
        google: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
        custom: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
    }),
    pageLoading: PropTypes.bool,
    children: PropTypes.node,
};

const defaultProps = {
    page: null,
    pageLoading: false,
    fonts: {
        custom: {
            families: ['Agrandir', 'GarageGothic-Bold'],
        },
    },
    children: null,
};

const MainLayout = ({ page, fonts, pageLoading, children }) => {
    const { loaded: fontsloaded = false } = useFonts(fonts);
    const { theme } = page || {};
    const { colors, elementsColor } = theme || {};
    const { loading = false } = useLoading();

    const { setHeaderHidden, setScrollDirection } = useHeader();
    const { fullscreen = false } = useFloatingVideo();
    const [visible, setVisible] = useState(false);
    const [footerIntersecting, setFooterIntersecting] = useState(false);

    // Trigger header on page end
    const onChange = useCallback(
        (intersecting, pct) => {
            if (intersecting && pct > 0.9 && !fullscreen) {
                setScrollDirection('up');
                setHeaderHidden(false);
            }
            setFooterIntersecting(intersecting);
        },
        [fullscreen, setHeaderHidden, setScrollDirection],
    );

    useEffect(() => {
        const { primary = null, secondary = null } = colors || {};
        if (primary !== null) {
            document.documentElement.style.setProperty('--gradientStop1', primary);
        }
        if (secondary !== null) {
            document.documentElement.style.setProperty('--gradientStop2', secondary);
        }
    }, [colors]);

    useEffect(() => {
        const id = setTimeout(() => {
            setVisible(true);
        }, 2100);
        return () => {
            clearTimeout(id);
        };
    }, [setVisible]);

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.fontsLoading]: !fontsloaded,
                },
            ])}
            style={{
                color: elementsColor,
            }}
        >
            <Header
                className={styles.header}
                inverted={elementsColor === 'black'}
                loading={pageLoading || loading}
            />
            <main
                className={classNames([
                    styles.content,
                    {
                        [styles.pageLoading]: loading,
                    },
                ])}
            >
                {children}
            </main>
            <Detector onChange={onChange} threshold={[0, 0.8, 0.9, 0.95, 1]}>
                <NewsletterFooter className={styles.footer} visible={footerIntersecting} />
            </Detector>
            {/* <Footer className={styles.footer} /> */}
            <FloatingVideo
                className={classNames([
                    styles.video,
                    {
                        // [styles.withDelay]: loading,
                        [styles.show]: visible,
                    },
                ])}
            />
            <FloatingVideoSlot
                fallback
                fixed
                adaptative
                className={styles.bottomSlot}
                name="bottom"
            />
            <Modal />
        </div>
    );
};

MainLayout.propTypes = propTypes;
MainLayout.defaultProps = defaultProps;

export default MainLayout;
